import { useCallback } from "react";
import { Select, type SelectProps } from "@mantine/core";
import {
  DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
  PortalNotificationPreference,
} from "../../../services/admin/portalAccess.service";
import {
  PortalNotificationPreferenceComboboxItem,
  PortalNotificationPreferenceOption,
} from "./PortalNotificationPreferenceOption";

const NOTIFICATION_PREFERENCE_OPTIONS: PortalNotificationPreferenceComboboxItem[] =
  [
    {
      value: "both",
      label: "Both",
      description: "Send immediate emails and include a daily summary.",
    },
    {
      value: "daily_digest",
      label: "Daily summary",
      description: "Send one summary at 4:00 PM Florida time.",
    },
    {
      value: "per_report",
      label: "Every report",
      description: "Send an email as soon as each report is available.",
    },
  ];

const NOTIFICATION_COMBOBOX_PROPS: SelectProps["comboboxProps"] = {
  withinPortal: true,
  position: "bottom-start",
  offset: 8,
};

interface PortalNotificationPreferenceSelectProps
  extends Omit<SelectProps, "data" | "defaultValue" | "onChange" | "value"> {
  value?: PortalNotificationPreference | null;
  onChange?: (value: PortalNotificationPreference | null) => void;
}

function usePreferenceChange(
  onChange?: (value: PortalNotificationPreference | null) => void
) {
  return useCallback(
    (nextValue: string | null) => {
      onChange?.(nextValue as PortalNotificationPreference | null);
    },
    [onChange]
  );
}

export function PortalNotificationPreferenceSelect(
  props: PortalNotificationPreferenceSelectProps
) {
  const { value, onChange, ...selectProps } = props;
  const handleChange = usePreferenceChange(onChange);

  return (
    <Select
      allowDeselect={false}
      comboboxProps={NOTIFICATION_COMBOBOX_PROPS}
      data={NOTIFICATION_PREFERENCE_OPTIONS}
      defaultValue={DEFAULT_PORTAL_NOTIFICATION_PREFERENCE}
      label="Notifications"
      onChange={handleChange}
      renderOption={PortalNotificationPreferenceOption}
      {...selectProps}
      value={value}
    />
  );
}
